import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { BadgeCheck, Loader2, Check } from "lucide-react";
import { useI18n } from "../i18n";

export const AdhesionSection = () => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const adhere = async () => {
    if (!name || !email) {
      toast.error(t("adh.err_fill"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/adhesion/checkout", { name, email, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("adh.err_start"));
      setLoading(false);
    }
  };

  const benefits = [t("adh.b1"), t("adh.b2"), t("adh.b3"), t("adh.b4")];

  return (
    <section id="adhesion" className="py-24 sm:py-32 bg-[#0B1121] relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-[0.12] pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#001489] rounded-full blur-[130px] opacity-40" />

      <div className="max-w-5xl mx-auto px-5 sm:px-8 relative grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#38BDF8] mb-4">{t("adh.eyebrow")}</p>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
            {t("adh.title")}
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-md leading-relaxed">{t("adh.desc")}</p>
          <ul className="mt-8 space-y-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-center gap-3 text-white/85">
                <span className="w-6 h-6 rounded-full bg-[#001489] flex items-center justify-center shrink-0">
                  <Check size={14} className="text-white" />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white/70 backdrop-blur-xl border border-white/20 rounded-3xl p-8 sm:p-10 shadow-2xl">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-black text-6xl text-[#0B1121]">5€</span>
            <span className="font-mono-accent text-xs uppercase tracking-widest text-slate-500">{t("adh.per")}</span>
          </div>
          <p className="mt-2 text-slate-600">{t("adh.priceNote")}</p>

          <div className="mt-6 space-y-3">
            <Input data-testid="adhesion-name" placeholder={t("adh.name")} value={name}
              onChange={(e) => setName(e.target.value)} className="h-12 bg-white" />
            <Input data-testid="adhesion-email" type="email" placeholder={t("sup.email")} value={email}
              onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white" />
          </div>

          <button
            data-testid="adhesion-submit"
            onClick={adhere}
            disabled={loading}
            className="mt-5 w-full h-14 bg-[#0B1121] text-white font-semibold rounded-xl hover:bg-[#001489] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <><BadgeCheck size={20} /> {t("adh.submit")}</>}
          </button>
          <p className="mt-3 text-xs text-slate-500 text-center">{t("adh.redirect")}</p>
        </div>
      </div>
    </section>
  );
};
